import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 320;
const MARGIN = { top: 20, right: 20, bottom: 50, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function PercentileBands({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const dates = data.dates;

    // Collect all values for Y domain
    const allValues = [
      ...data.min,
      ...data.max,
    ].filter((v) => v != null);

    const priceMin = d3.min(allValues);
    const priceMax = d3.max(allValues);
    const pricePadding = (priceMax - priceMin) * 0.1 || 1;

    // Scales
    const x = d3.scalePoint()
      .domain(dates)
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([priceMin - pricePadding, priceMax + pricePadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat((d) => formatDateLabel(d)))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickFormat((v) => `$${v}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Helper to build area data
    const buildAreaData = (lowerArr, upperArr) =>
      dates.map((date, i) => ({
        date,
        lower: lowerArr[i],
        upper: upperArr[i],
      }));

    const areaGen = d3.area()
      .x((d) => x(d.date))
      .y0((d) => y(d.lower))
      .y1((d) => y(d.upper));

    // Band 1: Min-Max (outermost)
    g.append('path')
      .datum(buildAreaData(data.min, data.max))
      .attr('d', areaGen)
      .attr('fill', '#f3f4f6')
      .attr('stroke', 'none');

    // Band 2: P10-P90
    g.append('path')
      .datum(buildAreaData(data.p10, data.p90))
      .attr('d', areaGen)
      .attr('fill', '#dbeafe')
      .attr('fill-opacity', 0.5)
      .attr('stroke', 'none');

    // Band 3: P25-P75 (IQR)
    g.append('path')
      .datum(buildAreaData(data.p25, data.p75))
      .attr('d', areaGen)
      .attr('fill', '#93c5fd')
      .attr('fill-opacity', 0.5)
      .attr('stroke', 'none');

    // Median line (dashed)
    const medianLine = d3.line()
      .x((d, i) => x(dates[i]))
      .y((d) => y(d))
      .defined((d) => d != null);

    g.append('path')
      .datum(data.median)
      .attr('d', medianLine)
      .attr('fill', 'none')
      .attr('stroke', '#6b7280')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,3');

    // Average line (solid)
    const avgLine = d3.line()
      .x((d, i) => x(dates[i]))
      .y((d) => y(d))
      .defined((d) => d != null);

    g.append('path')
      .datum(data.avg)
      .attr('d', avgLine)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2.5);

    // Dots on average line
    data.avg.forEach((v, i) => {
      if (v == null) return;
      g.append('circle')
        .attr('cx', x(dates[i]))
        .attr('cy', y(v))
        .attr('r', 3)
        .attr('fill', '#2563eb');
    });

    // Invisible overlay columns for tooltip hover + crosshair
    const colWidth = dates.length > 1 ? innerW / (dates.length - 1) : innerW;

    // Crosshair line (initially hidden)
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    dates.forEach((date, i) => {
      const cx = x(date);
      const halfW = colWidth / 2;

      g.append('rect')
        .attr('x', cx - halfW)
        .attr('y', 0)
        .attr('width', colWidth)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          crosshair
            .attr('x1', cx)
            .attr('x2', cx)
            .style('display', null);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date,
            max: data.max[i],
            p90: data.p90[i],
            p75: data.p75[i],
            avg: data.avg[i],
            median: data.median[i],
            p25: data.p25[i],
            p10: data.p10[i],
            min: data.min[i],
          });
        })
        .on('mouseleave', () => {
          crosshair.style('display', 'none');
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Percentile Bands</div>
          <div style={styles.subtitle}>Distribution of pest365 pricing across addresses over time</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No percentile data available
        </div>
      </div>
    );
  }

  const tooltipRows = tooltip
    ? [
        { label: 'Max', value: tooltip.max, color: '#9ca3af' },
        { label: 'P90', value: tooltip.p90, color: '#93c5fd' },
        { label: 'P75', value: tooltip.p75, color: '#60a5fa' },
        { label: 'Avg', value: tooltip.avg, color: '#2563eb' },
        { label: 'Median', value: tooltip.median, color: '#6b7280' },
        { label: 'P25', value: tooltip.p25, color: '#60a5fa' },
        { label: 'P10', value: tooltip.p10, color: '#93c5fd' },
        { label: 'Min', value: tooltip.min, color: '#9ca3af' },
      ]
    : [];

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Percentile Bands</div>
        <div style={styles.subtitle}>Distribution of pest365 pricing across addresses over time</div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2.5" />
          </svg>
          <span>Average</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#6b7280" strokeWidth="1.5" strokeDasharray="4,3" />
          </svg>
          <span>Median</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#93c5fd" fillOpacity="0.5" rx="2" />
          </svg>
          <span>P25-P75 (IQR)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#dbeafe" fillOpacity="0.5" rx="2" />
          </svg>
          <span>P10-P90</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#f3f4f6" rx="2" />
          </svg>
          <span>Min-Max</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltipRows.map((row) => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: row.color, fontWeight: row.label === 'Avg' ? 600 : 400 }}>
                {row.label}
              </span>
              <span style={{ fontWeight: 600 }}>
                ${row.value != null ? row.value.toFixed(2) : '--'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
